import { gerarTemplate } from './gerar';
import { getPrinter } from './printers';

export const ZETIQ_FORMATO = 'zetiq';
export const ZETIQ_VERSAO = 1;

export const uid = () => Math.random().toString(36).slice(2, 10);

const ROTACOES = new Set([0, 90, 180, 270]);

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

function texto(name, content, x, y, w, fontSize, align = 'left') {
  return {
    id: uid(),
    type: 'text',
    name,
    content,
    x,
    y,
    w,
    h: +(fontSize * 1.3).toFixed(2),
    fontSize,
    align,
    bold: false,
    maxLines: 1,
    font: '0',
    rotation: 0,
  };
}

export function modeloPadrao() {
  return {
    printerId: 'zebra-zd220',
    roll: {
      id: 'bobina-110-2x50',
      name: '110 mm — 2 colunas 50×30',
      rollWidth: 110,
      labelWidth: 50,
      labelHeight: 30,
      columns: 2,
      hGap: 4,
      vGap: 3,
      autoCenter: true,
      manualLeftMargin: 3,
      offsetX: 0,
      offsetY: 0,
    },
    elements: [
      texto('Descrição', '{produto1.descricao-1}', 2, 1.5, 46, 3),
      texto('Preço', '{produto1.precovarejo}', 2, 6, 46, 6, 'right'),
      {
        id: uid(),
        type: 'barcode',
        name: 'EAN',
        symbology: 'EAN13',
        content: '{produto1.ean}',
        x: 2,
        y: 14,
        w: 38,
        h: 12,
        showText: true,
        moduleWidth: 2,
        rotation: 0,
      },
    ],
    erp: { tamanhoDescricao: '22' },
  };
}

function normalizarElemento(el) {
  if (!el || typeof el !== 'object') return null;
  const rotation = ROTACOES.has(el.rotation) ? el.rotation : null;
  if (rotation === null) return null;
  const base = {
    id: String(el.id || uid()).slice(0, 64),
    name: String(el.name || 'Elemento').slice(0, 120),
    x: num(el.x),
    y: num(el.y),
    w: Math.max(0, num(el.w)),
    h: Math.max(0, num(el.h)),
    rotation,
  };
  if (el.type === 'text') {
    const align = ['left', 'center', 'right'].includes(el.align) ? el.align : 'left';
    return {
      ...base,
      type: 'text',
      content: String(el.content || '').slice(0, 2000),
      font: '0',
      fontSize: Math.max(0.5, num(el.fontSize, 3)),
      align,
      bold: Boolean(el.bold),
      maxLines: Math.min(20, Math.max(1, Math.round(num(el.maxLines, 1)))),
    };
  }
  if (el.type === 'barcode') {
    if (el.symbology !== 'EAN13' && el.symbology !== 'CODE128') return null;
    const item = {
      ...base,
      type: 'barcode',
      symbology: el.symbology,
      content: String(el.content || '').slice(0, 200),
      showText: el.showText !== false,
      moduleWidth: Math.min(10, Math.max(1, Math.round(num(el.moduleWidth, 2)))),
    };
    if (el.textFontSize !== undefined && el.textFontSize !== null && el.textFontSize !== '') {
      item.textFontSize = Math.max(0.5, num(el.textFontSize, 2));
    }
    return item;
  }
  if (el.type === 'line') {
    if (el.orientation !== 'h' && el.orientation !== 'v') return null;
    return { ...base, type: 'line', orientation: el.orientation, thickness: Math.max(0.1, num(el.thickness, 0.3)) };
  }
  if (el.type === 'rect') {
    return { ...base, type: 'rect', thickness: Math.max(0.1, num(el.thickness, 0.3)) };
  }
  return null;
}

export function validarLayout(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ok: false, error: 'O desenho da etiqueta é inválido.' };
  }
  const roll = raw.roll;
  if (!roll || typeof roll !== 'object') return { ok: false, error: 'A bobina do desenho está ausente.' };
  if (![1, 2, 3].includes(roll.columns)) return { ok: false, error: 'As colunas do desenho devem ser 1, 2 ou 3.' };
  if (!Array.isArray(raw.elements) || raw.elements.length > 300) {
    return { ok: false, error: 'A lista de elementos do desenho é inválida.' };
  }
  const elements = [];
  for (const el of raw.elements) {
    const parsed = normalizarElemento(el);
    if (!parsed) return { ok: false, error: 'Há um elemento inválido no desenho.' };
    elements.push(parsed);
  }
  const printer = getPrinter(raw.printerId);
  return {
    ok: true,
    layout: {
      printerId: printer.id,
      roll: {
        id: String(roll.id || 'bobina').slice(0, 64),
        name: String(roll.name || 'Bobina').slice(0, 120),
        rollWidth: num(roll.rollWidth),
        labelWidth: num(roll.labelWidth),
        labelHeight: num(roll.labelHeight),
        columns: roll.columns,
        hGap: num(roll.hGap),
        vGap: num(roll.vGap),
        autoCenter: roll.autoCenter !== false,
        manualLeftMargin: num(roll.manualLeftMargin),
        offsetX: num(roll.offsetX),
        offsetY: num(roll.offsetY),
      },
      elements,
      erp: {
        tamanhoDescricao: String(raw.erp?.tamanhoDescricao ?? '').trim().slice(0, 4),
      },
    },
  };
}

export function lerLayoutSalvo(json) {
  if (!json) return { ok: false, error: 'Este modelo não tem desenho gráfico.' };
  let raw;
  try {
    raw = JSON.parse(json);
  } catch {
    return { ok: false, error: 'O desenho salvo está corrompido.' };
  }
  return validarLayout(raw);
}

export function montarArquivoZetiq({ descricao, layout, template, qtdColunas }) {
  return {
    formato: ZETIQ_FORMATO,
    versao: ZETIQ_VERSAO,
    descricao,
    qtdColunas: layout ? layout.roll.columns : qtdColunas,
    layout: layout || null,
    template: layout ? gerarTemplate(layout) : (template || ''),
  };
}

export function nomeArquivoZetiq(descricao) {
  const base = String(descricao || 'etiqueta')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60) || 'etiqueta';
  return `${base}.zetiq`;
}

export function lerZetiq(text) {
  if (typeof text !== 'string' || text.length > 1_000_000) {
    return { ok: false, error: 'O arquivo excede 1 MB.' };
  }
  let raw;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, error: 'O arquivo .zetiq não é um JSON válido.' };
  }
  if (!raw || typeof raw !== 'object' || raw.formato !== ZETIQ_FORMATO) {
    return { ok: false, error: 'Este arquivo não é um modelo .zetiq do ZenGestão.' };
  }
  if (raw.versao !== ZETIQ_VERSAO) {
    return { ok: false, error: 'A versão deste arquivo .zetiq não é suportada.' };
  }
  const descricao = String(raw.descricao || '').trim();
  if (!descricao) return { ok: false, error: 'O arquivo não informa o nome do modelo.' };
  if (raw.layout) {
    const layout = validarLayout(raw.layout);
    if (!layout.ok) return layout;
    return {
      ok: true,
      descricao,
      layout: layout.layout,
      qtdColunas: layout.layout.roll.columns,
      template: '',
    };
  }
  const template = String(raw.template || '');
  if (!template.trim()) return { ok: false, error: 'O arquivo não tem desenho nem código de impressão.' };
  const qtd = Math.round(Number(raw.qtdColunas));
  return {
    ok: true,
    descricao,
    layout: null,
    qtdColunas: Math.min(6, Math.max(1, Number.isFinite(qtd) ? qtd : 1)),
    template,
  };
}

export function payloadDoLayout(descricao, layout) {
  return {
    descricao: descricao.trim(),
    qtdColunas: layout.roll.columns,
    template: gerarTemplate(layout),
    layoutJson: JSON.stringify(layout),
    atualizarLayout: true,
  };
}
